/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import {
	addToCartFetcherV2 as addToCartFetcher,
	BASE_ADD_2_CART_BODY,
	useCartSWRKey,
} from '@/data/Content/Cart';
import { personMutatorKeyMatcher } from '@/data/Content/Login';
import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useLoginRedirectRequired } from '@/data/Content/_LoginRedirectRequired';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import { fetchDefaultWishlistOrCreateNew, wishListUpdater } from '@/data/Content/_Wishlists';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { COOKIES } from '@/data/constants/cookie';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { TYPES } from '@/data/constants/product';
import { ONE_QUANTITY } from '@/data/constants/saveForLaterList';
import { useEventsContext } from '@/data/context/events';
import { CookiesSingletonContext } from '@/data/cookie/cookiesSingletonProvider';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { Order } from '@/data/types/Order';
import { ProductAttributeValue, ProductType, ResponseProductAttribute } from '@/data/types/Product';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getContractIdParamFromContext } from '@/data/utils/getContractIdParamFromContext';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { expand, shrink } from '@/data/utils/keyUtil';
import { mapProductDetailsData } from '@/data/utils/mapProductDetailsData';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { useTrackAddToCart } from 'presentation/events/hooks/useTrackAddToCart';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getFinalColorCode, OrderItemInput } from 'shared/custom/utils/colorSharedUtils';
import { getCookieEventId, getStoreIdAndStateId } from 'shared/custom/utils/cookieHandler';
import useSWR, { mutate, unstable_serialize as serialize, useSWRConfig } from 'swr';
import { useEstimatedDeliveryDates } from '@/data/Content/EstimatedDeliveryDates';

const DATA_KEY = 'FullProductLookup';

const findSkuBySwatch = (
	root: ProductType | undefined,
	swatch: ProductAttributeValue | undefined
) => {
	const found =
		swatch && root
			? root.items?.find(({ attributes }) =>
					attributes?.find(({ values }) => values.find(({ id }) => id === swatch.id))
			  )
			: undefined;
	return found;
};

export const useProductCard = (
	product: ProductType,
	isEstimatedDeliveryDateDisplayed?: boolean
) => {
	const trackAddToCart = useTrackAddToCart();
	const trackException = useTrackException();
	const { onAddToWishlist: onAddToWishlistEvent } = useEventsContext();
	const { setCookie } = useContext(CookiesSingletonContext);
	const wlNls = useLocalization('WishList');
	const success = useLocalization('success-message');
	const router = useNextRouter();
	const { settings } = useSettings();
	const {
		langId,
		storeId,
		defaultCurrency: currency,
		defaultCatalogId: catalogId,
	} = getClientSideCommon(settings, router);
	const { user } = useUser();
	const currentCartSWRKey = useCartSWRKey(); // in current language
	const { cache } = useSWRConfig();
	const isGenericUser = user?.isGeneric ?? false;
	const { loginStatus, redirectToLoginIfNeed: loginRequired } = useLoginRedirectRequired();
	const requestParams = useExtraRequestParameters();
	const [swatch, setSwatch] = useState<ProductAttributeValue>();
	const [root, setRoot] = useState<ProductType | undefined>(product.items ? product : undefined);
	const { showSuccessMessage, notifyError, notifyAddToCart } = useNotifications();
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const [isAddingToWishlist, setIsAddingToWishlist] = useState(false);
	const { colorClassification, lineRangeColors } = usePaintColors();
	const estimatedDeliveryDates = useEstimatedDeliveryDates(product, {
		enabled: !!isEstimatedDeliveryDateDisplayed,
	});

	const { data } = useSWR(
		!root?.items && storeId && product.partNumber && (product.hasSingleSKU || swatch)
			? [
					shrink({
						storeId,
						currency,
						catalogId,
						langId,
						...getContractIdParamFromContext(user?.context),
						...getCurrencyParamFromContext(user?.context),
						partNumber: product.partNumber,
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => productFetcher(true)(expand(props), requestParams)
	);
	const sku = useMemo(() => {
		if (swatch) {
			return findSkuBySwatch(root, swatch);
		} else if (product.items) {
			const { selection } = mapProductDetailsData(product, undefined);
			return selection?.sku;
		}
	}, [product, root, swatch]);

	const onSwatch = useCallback(
		async (event: MouseEvent<HTMLElement>, _swatch: ProductAttributeValue) => {
			event.stopPropagation();
			setSwatch(_swatch);
		},
		[]
	);

	const onAddToCart = useCallback(
		(sku: ProductType, quantity?: number) => async (event: MouseEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();
			const kitComponents = sku.type === TYPES.kit ? sku?.components : [];

			const { partNumber, manufacturer, attributes, componentQuantities } =
				sku.type === TYPES.kit
					? kitComponents?.reduce(
							(acc, ele) => {
								acc.partNumber.push(ele.partNumber);
								acc.manufacturer.push(ele.manufacturer);
								acc.attributes.push(ele.attributes);
								acc.componentQuantities.push(parseInt(ele.quantity ?? '1', 10) || 1);
								return acc;
							},
							{
								partNumber: [],
								manufacturer: [],
								attributes: [],
								componentQuantities: [],
							} as {
								partNumber: string[];
								manufacturer: string[];
								attributes: ResponseProductAttribute[][];
								componentQuantities: number[];
							}
					  ) || {
							partNumber: [],
							manufacturer: [],
							attributes: [],
							componentQuantities: [],
					  }
					: {
							partNumber: [sku?.partNumber],
							manufacturer: [sku?.manufacturer],
							attributes: [sku?.attributes],
							componentQuantities: [1],
					  };
			if ((await loginRequired()) || partNumber.length === 0) {
				return;
			}

			const productQuantity = quantity !== undefined ? quantity : parseInt(ONE_QUANTITY, 10);
			const orderItems: OrderItemInput[] = [];
			partNumber.forEach((pn, index) => {
				orderItems.push({
					partNumber: pn,
					quantity: (componentQuantities[index] * productQuantity).toString(),
					manufacturer: manufacturer[index] || manufacturer[0],
					attributes: attributes[index] || attributes[0],
				});
			});
			const orderItemsPayload = orderItems.map((item) => ({
				partNumber: item.partNumber,
				quantity: item.quantity,
				xitem_colorCode: getFinalColorCode(item, colorClassification, lineRangeColors) ?? undefined,
			}));
			const eventId = getCookieEventId();
			setCookie(COOKIES.mfbt, eventId);
			const { stLocId } = getStoreIdAndStateId();
			const x_pstore = stLocId;
			const x_mfbt = eventId;
			const x_userAgent = window.navigator.userAgent;
			const data = {
				...BASE_ADD_2_CART_BODY,
				orderItem: orderItemsPayload,
				x_mfbt,
				x_pstore,
				x_userAgent,
			};

			try {
				setIsAddingToCart(true);

				const cachedCartData = cache.get(serialize(currentCartSWRKey))?.data as Order;
				const cartSnapshot = cachedCartData?.orderItem ?? [];

				await addToCartFetcher(isGenericUser)(settings.storeId, {}, data, requestParams);

				if (isGenericUser) {
					await mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
					await mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
				}

				trackAddToCart({
					product: sku,
					quantity: productQuantity.toString(),
					cachedCartItems: cartSnapshot,
					...estimatedDeliveryDates,
					isEstimatedDeliveryDateDisplayed,
				});

				await mutate(cartMutatorKeyMatcher(EMPTY_STRING));
				await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined);

				notifyAddToCart();
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			} finally {
				setIsAddingToCart(false);
			}
		},
		[
			cache,
			currentCartSWRKey,
			isGenericUser,
			loginRequired,
			notifyAddToCart,
			notifyError,
			requestParams,
			setCookie,
			settings.storeId,
			trackAddToCart,
			trackException,
			colorClassification,
			lineRangeColors,
			estimatedDeliveryDates,
			isEstimatedDeliveryDateDisplayed,
		]
	);

	const addToWishList = useCallback(
		async ({ partNumber, wishList }: { partNumber: string; wishList: WishlistWishlistItem }) => {
			try {
				setIsAddingToWishlist(true);
				const rc = await wishListUpdater(true)(
					settings?.storeId as string,
					wishList.uniqueID as string,
					{ item: [{ partNumber, quantityRequested: '1' } as any] },
					{ addItem: true },
					requestParams
				);
				if (rc) {
					showSuccessMessage(success.WISHLIST_ADD_SUCCESS.t([wishList.description as string]));
				}
				product && onAddToWishlistEvent(product, 1);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			} finally {
				setIsAddingToWishlist(false);
			}
		},
		[
			settings?.storeId,
			requestParams,
			product,
			onAddToWishlistEvent,
			showSuccessMessage,
			success.WISHLIST_ADD_SUCCESS,
			notifyError,
			trackException,
		]
	);

	const addToDefaultWishlist = useCallback(
		async ({ partNumber }: { partNumber: string }) => {
			try {
				const { wishlistId, wishlistName } = await fetchDefaultWishlistOrCreateNew(true)(
					settings?.storeId,
					undefined,
					wlNls.DefaultWishListName.t(),
					requestParams
				);

				await addToWishList({
					partNumber,
					wishList: {
						uniqueID: wishlistId,
						description: wishlistName,
					} as WishlistWishlistItem,
				});
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			}
		},
		[wlNls, settings?.storeId, requestParams, addToWishList, notifyError, trackException]
	);

	useEffect(() => {
		// we will cache the parent product once it's fetched so ignore any null-ish responses
		if (data) {
			const parent = extractContentsArray(data)[0];
			setRoot(parent);
		}
	}, [data]);

	return {
		onSwatch,
		sku,
		onAddToCart,
		loginStatus,
		addToWishList,
		addToDefaultWishlist,
		isAddingToCart,
		isAddingToWishlist,
	};
};
